import type { Player } from "@/lib/types";

/**
 * The linked-players chip strip on the `/feedback` shell.
 *
 * Ports the legacy `renderFeedbackLinkedStrip`
 * (`js/coaching/feedback.js`). Shows the roster players linked to
 * the signed-in account; when none are linked, shows the
 * "ask a coach" empty-state copy.
 *
 * This is a passive display strip — it does NOT switch anything.
 * The Development tab has its own player *selector* (a viewer
 * with multiple linked players picks which profile to view
 * there); this strip just shows who's linked.
 */

export type LinkedPlayersStripProps = {
  players: Player[];
};

function playerLabel(p: Player): string {
  return p.jersey_number
    ? `#${p.jersey_number} ${p.display_name}`
    : p.display_name;
}

export function LinkedPlayersStrip({ players }: LinkedPlayersStripProps) {
  if (players.length === 0) {
    return (
      <p
        className="text-ink-muted text-sm"
        data-testid="feedback-linked-empty"
      >
        No roster player is linked to your account yet. Ask a coach to
        link you.
      </p>
    );
  }
  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="feedback-linked-strip"
    >
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        Linked players
      </span>
      {players.map((p) => (
        <span
          key={p.id}
          data-feedback-linked-player={p.id}
          className="rounded px-2 py-0.5 text-xs font-display bg-surface text-ink"
        >
          {playerLabel(p)}
        </span>
      ))}
    </div>
  );
}
